import { readFilters } from './filters'
import { SOURCE_FILTERS_MASK, MODEL_FILTERS_MASK } from './constants'
import { SUBJECT_TYPES } from './config'

export type SubjectTypesType = (typeof SUBJECT_TYPES)[keyof typeof SUBJECT_TYPES]

export type AppRequestTypeType =
  | 'dataItems'
  | 'statItems'
  | 'update'
  | 'export'
  | 'schema'
  | 'dataHashes'
  | 'statHashes'
  | 'filterOptions'

export type QueryFactoryType = {
  type: AppRequestTypeType
  blockUID: string
  confidenceLevel?: number
  secondaryConfidenceLevel?: number
  filteredRowAverages?: boolean
  ctrlKey?: boolean
  filters?: object
  format?: string
  group?: string
  hashes?: unknown
  options?: Record<string, unknown>
  schema?: unknown
  shiftKey?: boolean
  split?: unknown
  subjectType: SubjectTypesType
  swap?: unknown
  title?: unknown
  uids?: string[]
  update?: unknown
  waveUids?: string[]
}

type GetFiltersProps = {
  waveUids?: unknown
  uids?: unknown
  filters?: object | { toJS(): object }
}

type GetFiltersReturnType = {
  filtersSource: { [k: string]: unknown }
  filtersUids: unknown
  filtersModel: { [k: string]: unknown }
}

type QueryOptionsType = Pick<QueryFactoryType, 'subjectType' | 'blockUID'>
type DataItemsQueryOptionsType = Pick<QueryFactoryType, 'subjectType' | 'hashes' | 'blockUID'>
type StandardQueryOptionsType =
  | Pick<QueryFactoryType, 'subjectType' | 'blockUID'>
  | Pick<QueryFactoryType, 'subjectType' | 'blockUID' | 'group' | 'split' | 'swap' | 'options'>
  | Pick<
      QueryFactoryType,
      | 'subjectType'
      | 'blockUID'
      | 'group'
      | 'split'
      | 'swap'
      | 'options'
      | 'confidenceLevel'
      | 'secondaryConfidenceLevel'
      | 'filteredRowAverages'
    >
type FilerOptionsQueryOptionsType = StandardQueryOptionsType
type SchemaQueryOptionsType = StandardQueryOptionsType
type DataHashesQueryOptionsType = StandardQueryOptionsType
type StatHashesQueryOptionsType = Pick<
  QueryFactoryType,
  'subjectType' | 'hashes' | 'blockUID' | 'confidenceLevel' | 'secondaryConfidenceLevel'
>
type StatItemsQueryOptionsType = Pick<QueryFactoryType, 'subjectType' | 'hashes' | 'blockUID'>
type ExportQueryOptionsType = Pick<
  QueryFactoryType,
  | 'subjectType'
  | 'confidenceLevel'
  | 'secondaryConfidenceLevel'
  | 'filteredRowAverages'
  | 'title'
  | 'schema'
  | 'blockUID'
  | 'group'
  | 'split'
  | 'swap'
  | 'options'
  | 'format'
  | 'ctrlKey'
  | 'shiftKey'
>

export type QueryResultPart =
  | ['insights:update', QueryOptionsType & Pick<QueryFactoryType, 'update'>]
  | ['insights:filterOptions', FilerOptionsQueryOptionsType & GetFiltersReturnType]
  | ['insights:dataItems', DataItemsQueryOptionsType]
  | ['insights:schema', SchemaQueryOptionsType & GetFiltersReturnType]
  | ['insights:dataHashes', DataHashesQueryOptionsType & GetFiltersReturnType]
  | ['insights:statHashes', StatHashesQueryOptionsType]
  | ['insights:statItems', StatItemsQueryOptionsType]
  | ['insights:export', ExportQueryOptionsType & GetFiltersReturnType]

export type QueryResultType = QueryResultPart[]

export type QueryFactory = { (props: Partial<QueryFactoryType>): QueryResultType }

export interface QueryResultMaker<T> {
  (queryOptions: T, filtersOptions?: GetFiltersProps): QueryResultType
}

/**
 * Construct a filter from
 *  - array of subject uids/waveUids
 *  - array o9f filters
 * returns a filters set
 */
export function getFilters(
  { waveUids, uids, filters }: GetFiltersProps = {},
  doEmptyFilterData: boolean = false
): GetFiltersReturnType {
  return {
    // Uids filters
    filtersUids: doEmptyFilterData
      ? {}
      : waveUids
      ? { 'insights:subject:waveUid': waveUids }
      : uids
      ? { 'insights:subject:uid': uids }
      : {},
    // Source filters
    filtersSource: readFilters(filters, SOURCE_FILTERS_MASK),
    // Model filters
    filtersModel: doEmptyFilterData ? {} : readFilters(filters, MODEL_FILTERS_MASK)
  }
}

/**
 * Construct update query
 *
 * @param {Object} update Update data
 * @returns Query
 */
const updateQuery = (queryOptions: QueryOptionsType, update: unknown): QueryResultType => [
  ['insights:update', { ...queryOptions, update }]
]
/**
 * Filter options query
 */
const filterOptionsQuery: QueryResultMaker<FilerOptionsQueryOptionsType> = (queryOptions, filtersOptions) => [
  [
    'insights:filterOptions',
    {
      ...queryOptions,
      ...getFilters(filtersOptions)
    }
  ]
]

const dataItemsQuery: QueryResultMaker<DataItemsQueryOptionsType> = queryOptions => [
  [
    'insights:dataItems',
    {
      ...queryOptions
    }
  ]
]

const statHashesQuery: QueryResultMaker<StatHashesQueryOptionsType> = queryOptions => [
  [
    'insights:statHashes',
    {
      ...queryOptions
    }
  ]
]

const statItemsQuery: QueryResultMaker<StatItemsQueryOptionsType> = queryOptions => [
  [
    'insights:statItems',
    {
      ...queryOptions
    }
  ]
]

/**
 * Construct export query data
 * @param exportData - Export data
 */
const exportQuery: QueryResultMaker<ExportQueryOptionsType> = (queryOptions, exportQueryOptions) => [
  [
    `insights:export`,
    {
      ...queryOptions,
      ...getFilters(exportQueryOptions)
    }
  ]
]

/**
 * Construct schema query
 */
const schemaQuery: QueryResultMaker<SchemaQueryOptionsType> = (queryOptions, filtersOptions) => [
  [
    `insights:schema`,
    {
      ...queryOptions,
      ...getFilters(filtersOptions)
    }
  ]
]

/**
 * Construct data query
 */
const dataHashesQuery: QueryResultMaker<DataHashesQueryOptionsType> = (queryOptions, filtersOptions) => [
  [
    `insights:dataHashes`,
    {
      ...queryOptions,
      ...getFilters(filtersOptions)
    }
  ]
]

function makeQueryFactory(queryFactoryType: SubjectTypesType) {
  return Object.assign(
    ({
      type,
      subjectType = queryFactoryType,
      update,
      filters,
      confidenceLevel,
      secondaryConfidenceLevel,
      filteredRowAverages,
      title,
      schema,
      blockUID,
      hashes,
      group,
      split,
      swap,
      options,
      format,
      shiftKey,
      ctrlKey,
      uids,
      waveUids
    }: Partial<QueryFactoryType>): QueryResultType => {
      let query = [] as QueryResultType
      switch (type) {
        case 'schema':
          query = schemaQuery(
            {
              subjectType,
              blockUID,
              group,
              split,
              swap,
              options
            },
            { uids, waveUids, filters }
          )
          break
        case 'dataHashes':
          query = dataHashesQuery(
            {
              subjectType,
              blockUID,
              confidenceLevel,
              secondaryConfidenceLevel,
              filteredRowAverages,
              group,
              split,
              swap,
              options
            },
            { uids, waveUids, filters }
          )
          break
        case 'filterOptions':
          query = filterOptionsQuery(
            {
              subjectType,
              blockUID,
              group,
              split,
              swap,
              options
            },
            { uids, waveUids, filters }
          )
          break
        case 'export':
          // debugger
          query = exportQuery(
            {
              subjectType,
              confidenceLevel,
              secondaryConfidenceLevel,
              filteredRowAverages,
              title,
              schema,
              blockUID,
              format,
              shiftKey,
              ctrlKey,
              group,
              split,
              swap,
              options
            },
            { uids, waveUids, filters }
          )
          break
        case 'dataItems':
          query = dataItemsQuery({ subjectType, hashes, blockUID })
          break
        case 'statHashes':
          query = statHashesQuery({ subjectType, hashes, blockUID, confidenceLevel, secondaryConfidenceLevel })
          break
        case 'statItems':
          query = statItemsQuery({ subjectType, hashes, blockUID })
          break
        case 'update':
          query = updateQuery({ subjectType, blockUID }, update)
          break
        default:
          throw new Error(`Unknown query type: ${type as unknown as string}`)
      }
      return query
    },
    {
      subjectType: queryFactoryType
    }
  )
}

const queryFactories: Partial<Record<SubjectTypesType, QueryFactory>> = {}

export const conceptQueryFactory = makeQueryFactory(SUBJECT_TYPES.CONCEPT)
export const waveQueryFactory = makeQueryFactory(SUBJECT_TYPES.WAVE)

export const getQueryFactory = (subjectType: SubjectTypesType): QueryFactory => {
  if (!(subjectType in queryFactories)) {
    queryFactories[subjectType] = makeQueryFactory(subjectType)
  }
  return queryFactories[subjectType]
}
